import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getAgreementBySignToken, signAgreementByToken, type Agreement } from '@/lib/api/agreements';

const fmtMoney = (v: number | null | undefined) =>
  v == null ? '—' : `$${Number(v).toLocaleString('en-CA', { minimumFractionDigits: 2 })}`;
const fmtDate = (d: string | null | undefined) =>
  d ? new Date(d + 'T12:00:00').toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }) : '—';

export const AgreementSignPage = () => {
  const { token } = useParams<{ token: string }>();
  const [agreement, setAgreement] = useState<Agreement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signerName, setSignerName] = useState('');
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    if (!token) return;
    getAgreementBySignToken(token)
      .then(a => {
        setAgreement(a);
        if (a.status === 'signed') setSigned(true);
      })
      .catch(err => {
        const msg = err?.response?.data?.error ?? 'This link is invalid or has expired.';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleSign = async () => {
    if (!token || !signerName.trim()) return;
    setSigning(true);
    try {
      await signAgreementByToken(token, signerName.trim());
      setSigned(true);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Signing failed. Please try again.');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-500">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm">Loading agreement…</p>
        </div>
      </div>
    );
  }

  if (error && !signed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl border border-red-200 p-8 max-w-md w-full text-center shadow-sm">
          <span className="material-symbols-outlined text-red-400 text-5xl mb-4 block">link_off</span>
          <h1 className="text-lg font-bold text-gray-900 mb-2">Link Unavailable</h1>
          <p className="text-sm text-gray-500">{error}</p>
          <p className="text-xs text-gray-400 mt-4">Please contact OchrePoint Contracting to request a new link.</p>
        </div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl border border-green-200 p-8 max-w-md w-full text-center shadow-sm">
          <span className="material-symbols-outlined text-green-500 text-5xl mb-4 block">task_alt</span>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Agreement Signed</h1>
          <p className="text-sm text-gray-500">
            {agreement?.signed_by_name
              ? `Signed by ${agreement.signed_by_name}.`
              : 'This agreement has been signed.'}{' '}
            OchrePoint Contracting has been notified.
          </p>
          <div className="mt-6 p-4 bg-gray-50 rounded-xl text-left text-xs text-gray-600 space-y-1">
            <p><span className="font-semibold">Agreement:</span> {agreement?.agreement_number}</p>
            <p><span className="font-semibold">Title:</span> {agreement?.title}</p>
          </div>
        </div>
      </div>
    );
  }

  const a = agreement!;
  const clauses = (a.clauses as any) ?? {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-3">
        <span className="material-symbols-outlined text-primary text-2xl">handshake</span>
        <div>
          <p className="text-xs text-gray-400 font-mono">{a.agreement_number}</p>
          <p className="text-sm font-bold text-gray-900">{a.title}</p>
        </div>
        <span className="ml-auto text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-0.5 rounded-full font-medium">
          Awaiting Signature
        </span>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Parties */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-bold text-gray-900 mb-4">Agreement Parties</h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Contractor</p>
              <p className="font-semibold text-gray-900">{a.company_name}</p>
              {a.company_email && <p className="text-sm text-gray-500">{a.company_email}</p>}
              {a.company_phone && <p className="text-sm text-gray-500">{a.company_phone}</p>}
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Client</p>
              <p className="font-semibold text-gray-900">{a.client_name}</p>
              {a.client_email && <p className="text-sm text-gray-500">{a.client_email}</p>}
              {a.client_phone && <p className="text-sm text-gray-500">{a.client_phone}</p>}
              {a.client_address && <p className="text-sm text-gray-400">{a.client_address}</p>}
            </div>
          </div>
        </div>

        {/* Project details */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-bold text-gray-900 mb-4">Project Details</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Agreement Date</p>
              <p className="font-medium text-gray-900">{fmtDate(a.agreement_date)}</p>
            </div>
            {a.start_date && (
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Start Date</p>
                <p className="font-medium text-gray-900">{fmtDate(a.start_date)}</p>
              </div>
            )}
            {a.estimated_end_date && (
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Est. Completion</p>
                <p className="font-medium text-gray-900">{fmtDate(a.estimated_end_date)}</p>
              </div>
            )}
            {a.contract_value != null && (
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Contract Value</p>
                <p className="font-semibold text-gray-900 text-base">{fmtMoney(a.contract_value)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Scope of work */}
        {a.scope_of_work && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="font-bold text-gray-900 mb-3">Scope of Work</h2>
            <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{a.scope_of_work}</p>
            {a.scope_exclusions && (
              <>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mt-4 mb-2">Exclusions</p>
                <p className="text-sm text-gray-600 whitespace-pre-line">{a.scope_exclusions}</p>
              </>
            )}
          </div>
        )}

        {/* Payment */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-bold text-gray-900 mb-3">Payment Terms</h2>
          <div className="grid grid-cols-3 gap-4 text-sm">
            {a.deposit_percent > 0 && (
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Deposit</p>
                <p className="font-medium text-gray-900">{a.deposit_percent}%</p>
              </div>
            )}
            {a.holdback_percent > 0 && (
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Holdback</p>
                <p className="font-medium text-gray-900">{a.holdback_percent}%</p>
              </div>
            )}
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Payment Due</p>
              <p className="font-medium text-gray-900">{a.payment_due_days ?? 30} days</p>
            </div>
          </div>
          {a.payment_notes && <p className="text-sm text-gray-500 mt-3">{a.payment_notes}</p>}
        </div>

        {/* Key clauses */}
        {clauses && Object.values(clauses).some((c: any) => c?.enabled) && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="font-bold text-gray-900 mb-4">Terms & Conditions</h2>
            <div className="space-y-4">
              {(['change_orders', 'warranty', 'dispute', 'lien_rights', 'additional'] as const).map(key => {
                const clause = clauses[key] as any;
                if (!clause?.enabled || !clause?.content) return null;
                const labels: Record<string, string> = {
                  change_orders: 'Change Orders', warranty: 'Warranty',
                  dispute: 'Dispute Resolution', lien_rights: 'Lien Rights', additional: 'Additional Terms',
                };
                return (
                  <div key={key}>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{labels[key]}</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{clause.content}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Signing box */}
        <div className="bg-white rounded-2xl border-2 border-primary/30 p-6 shadow-sm">
          <h2 className="font-bold text-gray-900 mb-1">Sign Agreement</h2>
          <p className="text-sm text-gray-500 mb-5">
            By signing below you confirm you have read and agree to all terms of this agreement.
            Your name, the date, and your IP address will be recorded as your electronic signature.
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={signerName}
                onChange={e => setSignerName(e.target.value)}
                placeholder="Type your full legal name"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              onClick={handleSign}
              disabled={signing || !signerName.trim()}
              className="w-full py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {signing
                ? <><span className="material-symbols-outlined text-base animate-spin">sync</span> Signing…</>
                : <><span className="material-symbols-outlined text-base">draw</span> Sign Agreement</>
              }
            </button>
            <p className="text-xs text-center text-gray-400">
              This is a legally binding electronic signature under British Columbia law.
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 pb-8">
          OchrePoint Contracting LTD · Duncan, BC · {a.company_email}
        </p>
      </div>
    </div>
  );
};
